import Axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';
import type { paths } from '@tipman-pickem/api-types';

const STORAGE_KEY_ACCESS_TOKEN = 'access_token';

type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete';

type PathsWithMethod<Method extends HttpMethod> = {
  [Path in keyof paths]: paths[Path] extends { [K in Method]: any } ? Path : never;
}[keyof paths];

type RequestBody<Path extends keyof paths, Method extends HttpMethod> = paths[Path] extends {
  [K in Method]: { requestBody?: { content: { 'application/json': infer Body } } };
}
  ? Body
  : never;

type ResponseBody<Path extends keyof paths, Method extends HttpMethod> = paths[Path] extends {
  [K in Method]: { responses: { 200: { content: { 'application/json': infer Body } } } };
}
  ? Body
  : paths[Path] extends {
        [K in Method]: { responses: { 201: { content: { 'application/json': infer Body } } } };
      }
    ? Body
    : unknown;

type QueryParams<Path extends keyof paths, Method extends HttpMethod> = paths[Path] extends {
  [K in Method]: { parameters: { query?: infer Query } };
}
  ? Query
  : never;

type PathParams<Path extends keyof paths, Method extends HttpMethod> = paths[Path] extends {
  [K in Method]: { parameters: { path?: infer PathParam } };
}
  ? PathParam
  : never;

interface RequestConfig<Path extends keyof paths, Method extends HttpMethod> {
  params?: QueryParams<Path, Method>;
  pathParams?: PathParams<Path, Method>;
  config?: Omit<AxiosRequestConfig, 'params'>;
}

class TypedApiClient {
  private axiosInstance;

  constructor(baseURL: string) {
    this.axiosInstance = Axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(this.authRequestInterceptor);
    this.axiosInstance.interceptors.response.use(
      response => response.data,
      error => {
        // Handle 401 Unauthorized - redirect to login
        if (error.response?.status === 401) {
          localStorage.removeItem(STORAGE_KEY_ACCESS_TOKEN);
          window.location.href = '/login';
          return Promise.reject({ message: 'Session expired. Please log in again.' });
        }

        const message =
          error.response?.data?.error || error.response?.data?.message || 'Something went wrong';
        return Promise.reject({ message });
      }
    );
  }

  private authRequestInterceptor(config: InternalAxiosRequestConfig) {
    if (config.headers) {
      config.headers.Accept = 'application/json';
    }

    const accessToken = localStorage.getItem(STORAGE_KEY_ACCESS_TOKEN);
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    if (config.params) {
      const numericFields = ['limit', 'offset', 'page', 'pageSize', 'count', 'skip'];
      Object.keys(config.params).forEach(key => {
        if (numericFields.includes(key) && config.params[key] !== undefined) {
          const value = config.params[key];
          const numValue = Number(value);
          if (!isNaN(numValue)) {
            config.params[key] = numValue;
          }
        }
      });
    }

    return config;
  }

  private buildUrl<Path extends keyof paths, Method extends HttpMethod>(
    path: Path,
    pathParams?: PathParams<Path, Method>
  ): string {
    if (!pathParams) return path as string;

    let url = path as string;
    Object.entries(pathParams).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, String(value));
    });
    return url;
  }

  async get<Path extends PathsWithMethod<'get'>>(
    path: Path,
    options?: RequestConfig<Path, 'get'>
  ): Promise<ResponseBody<Path, 'get'>> {
    const url = this.buildUrl(path, options?.pathParams);
    return this.axiosInstance.get(url, {
      params: options?.params,
      ...options?.config,
    });
  }

  async post<Path extends PathsWithMethod<'post'>>(
    path: Path,
    data?: RequestBody<Path, 'post'>,
    options?: RequestConfig<Path, 'post'>
  ): Promise<ResponseBody<Path, 'post'>> {
    const url = this.buildUrl(path, options?.pathParams);
    return this.axiosInstance.post(url, data, {
      params: options?.params,
      ...options?.config,
    });
  }

  async put<Path extends PathsWithMethod<'put'>>(
    path: Path,
    data?: RequestBody<Path, 'put'>,
    options?: RequestConfig<Path, 'put'>
  ): Promise<ResponseBody<Path, 'put'>> {
    const url = this.buildUrl(path, options?.pathParams);
    return this.axiosInstance.put(url, data, {
      params: options?.params,
      ...options?.config,
    });
  }

  async patch<Path extends PathsWithMethod<'patch'>>(
    path: Path,
    data?: RequestBody<Path, 'patch'>,
    options?: RequestConfig<Path, 'patch'>
  ): Promise<ResponseBody<Path, 'patch'>> {
    const url = this.buildUrl(path, options?.pathParams);
    return this.axiosInstance.patch(url, data, {
      params: options?.params,
      ...options?.config,
    });
  }

  async delete<Path extends PathsWithMethod<'delete'>>(
    path: Path,
    options?: RequestConfig<Path, 'delete'>
  ): Promise<ResponseBody<Path, 'delete'>> {
    const url = this.buildUrl(path, options?.pathParams);
    return this.axiosInstance.delete(url, {
      params: options?.params,
      ...options?.config,
    });
  }
}

export const apiClient = new TypedApiClient(env.API_BASE_URL);

export type { ResponseBody, RequestBody, QueryParams, PathParams };
